import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Divider,
  Box,
  Chip,
  Stack,
} from "@mui/material";

import { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";

export default function PlannerProjectsHistoryDialog({
  open,
  onClose,
  projects = [],
  selectedClass = null,
}) {
  const theme = useTheme();

  const [search, setSearch] = useState("");

  const filteredProjects = useMemo(() => {
    return [...projects]
      .filter((project) => {
        const value = search.trim().toLowerCase();

        if (!value) {
          return true;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDate = new Date(project.startDate);
        startDate.setHours(0, 0, 0, 0);

        const dueDate = new Date(project.dueDate);
        dueDate.setHours(0, 0, 0, 0);

        const isPast = dueDate < today;
        const isFuture = startDate > today;
        const isOngoing = !isPast && !isFuture;

        const isCompletedSearch = [
          "abgeschlossen",
          "abgelaufen",
          "fertig",
          "done",
        ].some((keyword) => keyword.startsWith(value));

        const isOngoingSearch = ["laufend", "aktiv", "current"].some(
          (keyword) => keyword.startsWith(value),
        );

        const isFutureSearch = ["geplant", "zukunft", "future"].some(
          (keyword) => keyword.startsWith(value),
        );

        const statusSearch =
          isCompletedSearch || isOngoingSearch || isFutureSearch;

        const matchesStatus =
          (isCompletedSearch && isPast) ||
          (isOngoingSearch && isOngoing) ||
          (isFutureSearch && isFuture);

        if (statusSearch) {
          return matchesStatus;
        }

        return (
          project.title?.toLowerCase().includes(value) ||
          project.subjectName?.toLowerCase().includes(value) ||
          project.description?.toLowerCase().includes(value)
        );
      })
      .sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
      );
  }, [projects, search]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {selectedClass
          ? `Projektverlauf · ${selectedClass.name}`
          : "Projektverlauf"}
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Projekt, Fach, Status oder Beschreibung..."
          sx={{ mb: 2 }}
        />

        <Stack spacing={1}>
          {filteredProjects.map((project) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const startDate = new Date(project.startDate);
            startDate.setHours(0, 0, 0, 0);

            const dueDate = new Date(project.dueDate);
            dueDate.setHours(0, 0, 0, 0);

            const isPast = dueDate < today;
            const isFuture = startDate > today;
            const isOngoing = !isPast && !isFuture;

            let statusLabel = "";
            let statusColor = "";

            if (isPast) {
              statusLabel = "✓ Abgeschlossen";
              statusColor = theme.palette.success.main;
            } else if (isOngoing) {
              statusLabel = "● Laufend";
              statusColor = theme.palette.warning.main;
            } else {
              statusLabel = "○ Geplant";
              statusColor = theme.palette.info.main;
            }

            return (
              <Box
                key={project.id}
                sx={{
                  opacity: isPast ? 0.7 : 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography fontWeight={600}>{project.title}</Typography>

                  <Chip
                    label={statusLabel}
                    size="small"
                    sx={{
                      bgcolor: statusColor,
                      color: theme.palette.common.white,
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {project.subjectName}
                </Typography>

                {!selectedClass && (
                  <Typography variant="body2" color="text.secondary">
                    Klasse: {project.classes?.map((c) => c.name).join(", ")}
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary">
                  {new Date(project.startDate).toLocaleDateString("de-CH")}
                  {" → "}
                  {new Date(project.dueDate).toLocaleDateString("de-CH")}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {project.estimatedMinutes} Minuten
                  {/* {project.evaluation ? " · Bewertet ✅" : ""} */}
                  {project.evaluation ? " · bewertet" : ""}
                </Typography>

                {project.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    // sx={{
                    //   mt: 0.5,
                    // }}
                  >
                    Notiz: {project.description}
                  </Typography>
                )}

                <Divider sx={{ mt: 1 }} />
              </Box>
            );
          })}

          {filteredProjects.length === 0 && (
            <Typography color="text.secondary">
              Keine Projekte gefunden.
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
